import { DeviceError, DeviceErrorCode } from '../../../errors/DeviceError'
import { CH315_LAYOUT } from '../constants'
import {
  CH315_COMMAND,
  type CH315CommandResponse,
  type CH315KeyboardProtocol,
  type CH315MemoryReadResult,
} from '../protocols/ch315-keyboard'

export interface CH315MemoryProtocol {
  readEeprom(address: number, length: number): Promise<CH315MemoryReadResult>
  writeEeprom(address: number, data: Uint8Array): Promise<CH315CommandResponse>
  readFlashBulk(address: number, length: number): Promise<CH315MemoryReadResult>
  writeFlashBulk(address: number, data: Uint8Array): Promise<CH315CommandResponse>
}

export interface CH315MemoryWriteOptions {
  /** 写完后按相同传输方式读回并逐字节验证；默认 false。 */
  verifyReadback?: boolean
}

export interface CH315BulkMemoryWriteOptions extends CH315MemoryWriteOptions {
  /**
   * 显式发送一个 length=0 的 0xAF 收尾包。
   *
   * 调用方必须提供位于宏/图片/动画对象地址范围内的地址；配置写入不得设置此项。
   */
  finalizeAddress?: number
}

/** 不执行任何隐式硬件动作的 CH315 分块内存访问层。 */
export class CH315Memory {
  public constructor(private readonly protocol: CH315MemoryProtocol | CH315KeyboardProtocol) {}

  public read(address: number, length: number): Promise<Uint8Array> {
    return this.readChunks(address, length, false)
  }

  public readBulk(address: number, length: number): Promise<Uint8Array> {
    return this.readChunks(address, length, true)
  }

  public async write(
    address: number,
    data: Uint8Array,
    options: CH315MemoryWriteOptions = {},
  ): Promise<void> {
    assertWriteRange(address, data.byteLength)
    for (let offset = 0; offset < data.byteLength; offset += CH315_LAYOUT.normalWriteChunkBytes) {
      const chunk = data.slice(offset, offset + CH315_LAYOUT.normalWriteChunkBytes)
      const response = await this.protocol.writeEeprom(address + offset, chunk)
      assertWriteResponse(
        response,
        CH315_COMMAND.writeEeprom,
        address + offset,
        chunk.byteLength,
        false,
      )
    }
    if (options.verifyReadback && data.byteLength > 0) {
      await this.verify(address, data, false)
    }
  }

  public async writeBulk(
    address: number,
    data: Uint8Array,
    options: CH315BulkMemoryWriteOptions = {},
  ): Promise<void> {
    assertWriteRange(address, data.byteLength)
    for (let offset = 0; offset < data.byteLength; offset += CH315_LAYOUT.bulkDataChunkBytes) {
      const chunk = data.slice(offset, offset + CH315_LAYOUT.bulkDataChunkBytes)
      const response = await this.protocol.writeFlashBulk(address + offset, chunk)
      assertWriteResponse(
        response,
        CH315_COMMAND.writeFlashBulk,
        address + offset,
        chunk.byteLength,
        true,
      )
    }
    if (options.finalizeAddress !== undefined) {
      await this.finalizeBulkWrite(options.finalizeAddress)
    }
    if (options.verifyReadback && data.byteLength > 0) {
      await this.verify(address, data, true)
    }
  }

  /** 显式发送宏/图片/动画所需的 length=0 结束包。 */
  public async finalizeBulkWrite(address: number): Promise<void> {
    assertAddress(address)
    const response = await this.protocol.writeFlashBulk(address, new Uint8Array())
    assertWriteResponse(response, CH315_COMMAND.writeFlashBulk, address, 0, true)
  }

  private async readChunks(address: number, length: number, bulk: boolean): Promise<Uint8Array> {
    assertReadRange(address, length)
    const result = new Uint8Array(length)
    const chunkBytes = bulk ? CH315_LAYOUT.bulkDataChunkBytes : CH315_LAYOUT.normalReadChunkBytes

    for (let offset = 0; offset < length; offset += chunkBytes) {
      const expectedLength = Math.min(chunkBytes, length - offset)
      const expectedAddress = address + offset
      const chunk = bulk
        ? await this.protocol.readFlashBulk(expectedAddress, expectedLength)
        : await this.protocol.readEeprom(expectedAddress, expectedLength)
      assertReadResult(chunk, expectedAddress, expectedLength)
      result.set(chunk.data, offset)
    }
    return result
  }

  private async verify(address: number, expected: Uint8Array, bulk: boolean): Promise<void> {
    if (address % 4 !== 0) {
      throw invalidParam('CH315 readback verification requires a 4-byte aligned address')
    }
    const alignedLength = Math.ceil(expected.byteLength / 4) * 4
    assertReadRange(address, alignedLength)
    const actual = await this.readChunks(address, alignedLength, bulk)
    for (let offset = 0; offset < expected.byteLength; offset += 1) {
      if (actual[offset] !== expected[offset]) {
        throw new DeviceError(
          DeviceErrorCode.READ_FAILED,
          `CH315 readback mismatch at address 0x${(address + offset).toString(16)}`,
        )
      }
    }
  }
}

function assertReadResult(
  result: CH315MemoryReadResult,
  expectedAddress: number,
  expectedLength: number,
): void {
  if (
    result.address !== expectedAddress ||
    result.length !== expectedLength ||
    result.data.byteLength !== expectedLength
  ) {
    throw new DeviceError(
      DeviceErrorCode.READ_FAILED,
      `CH315 read response mismatch: expected ${expectedLength} bytes at 0x${expectedAddress.toString(16)}, received ${result.data.byteLength} bytes at 0x${result.address.toString(16)}`,
    )
  }
}

function assertWriteResponse(
  response: CH315CommandResponse,
  command: number,
  expectedAddress: number,
  expectedLength: number,
  bulk: boolean,
): void {
  const requiredLength = bulk ? 8 : 7
  if (
    response.command !== command ||
    response.payload[0] !== command ||
    response.payload.byteLength < requiredLength
  ) {
    throw new DeviceError(
      DeviceErrorCode.READ_FAILED,
      'CH315 write response is truncated or has the wrong command',
    )
  }
  const actualLength = bulk ? readUint16LE(response.payload, 2) : response.payload[2]
  const actualAddress = readUint32LE(response.payload, bulk ? 4 : 3)
  if (actualLength !== expectedLength || actualAddress !== expectedAddress) {
    throw new DeviceError(
      DeviceErrorCode.READ_FAILED,
      `CH315 write response mismatch: expected ${expectedLength} bytes at 0x${expectedAddress.toString(16)}, received ${actualLength} bytes at 0x${actualAddress.toString(16)}`,
    )
  }
}

function assertReadRange(address: number, length: number): void {
  assertAddress(address)
  if (address % 4 !== 0 || !Number.isInteger(length) || length < 0 || length % 4 !== 0) {
    throw invalidParam('CH315 reads require a 4-byte aligned address and length')
  }
  assertEndAddress(address, length)
}

function assertWriteRange(address: number, length: number): void {
  assertAddress(address)
  if (!Number.isInteger(length) || length < 0) {
    throw invalidParam('CH315 write length must be a non-negative integer')
  }
  assertEndAddress(address, length)
}

function assertAddress(address: number): void {
  if (!Number.isInteger(address) || address < 0 || address > 0xffffffff) {
    throw invalidParam(`Invalid CH315 address: ${address}`)
  }
}

function assertEndAddress(address: number, length: number): void {
  if (address + length > 0x100000000) {
    throw invalidParam('CH315 memory range exceeds uint32 address space')
  }
}

function readUint16LE(data: Uint8Array, offset: number): number {
  return data[offset] | (data[offset + 1] << 8)
}

function readUint32LE(data: Uint8Array, offset: number): number {
  return (
    (data[offset] |
      (data[offset + 1] << 8) |
      (data[offset + 2] << 16) |
      (data[offset + 3] << 24)) >>>
    0
  )
}

function invalidParam(message: string): DeviceError {
  return new DeviceError(DeviceErrorCode.INVALID_PARAM, message)
}
